import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Button,
  Chip,
  DialogActions,
  DialogTitle,
  Grid,
  IconButton,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { DynamicTooltipChip } from 'src/components/dynamicTooltipChip/DynamicTooltipChip';
import { Modal } from 'src/components/modal';
import { Progress } from 'src/components/progress';
import { ScaleContainer } from 'src/components/scaleContainer/ScaleContainer';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import {
  useGetAllLayoutType,
  useGetThemes,
  useResizeObserver,
} from 'src/hooks';
import { IStatus } from 'src/interfaces';
import {
  getComponentDisplayName,
  getLayoutChannelName,
  getLayoutTypeName,
  getScreenConfigurationLabel,
  lang,
  naturalSort,
  useUserRole,
} from 'src/utils';
import {
  assemblyEditFromInfoEvent,
  enterEditComponentFromInfoEvent,
} from 'src/utils/google-tag-manager';

import { PreviewModalContent } from './modules/previewModalContent';
import { PreviewModalSubTitle } from './modules/previewModalSubTitle';
import type { PreviewModalProps } from './PreviewModal.interface';
import {
  StyledDialogContent,
  StyledGrid,
  StyledSectionTypes,
  SubTitleContainer,
} from './PreviewModal.styled';

export const PreviewModal = (props: PreviewModalProps) => {
  const { data: themes = [] } = useGetThemes();
  const { containerRef, ...containerSize } = useResizeObserver();
  const { isViewOnly } = useUserRole();

  const { data: layoutTypes = [] } = useGetAllLayoutType();

  const advancedSettings = (sectionTypes: undefined | string[]) => {
    return (
      <>
        {sectionTypes?.map((str: string) => (
          <StyledSectionTypes key={str}>
            <Chip label={str} size="small" />
          </StyledSectionTypes>
        ))}
      </>
    );
  };

  const getSubTitles = (() => {
    switch (props.type) {
      case 'component':
        return [
          {
            label: lang.componentType,
            value:
              getComponentDisplayName(props.content?.component_type?.name) ??
              '',
          },
          {
            label: lang.size,
            value: lang.previewModal.sizeDescription({
              width: props.content?.width,
              height: props.content?.height,
            }),
          },
        ];

      case 'assembly':
        return [
          {
            label: lang.channel,
            value: getLayoutChannelName(props.content),
          },
          {
            label: lang.layoutType,
            value: getLayoutTypeName(props.content),
          },
        ];

      case 'layout':
        const sectionTypes = props.content?.config?.sectionTypes;

        return [
          {
            label: lang.channel,
            value: props.content.channel_name ?? '',
          },
          {
            label: lang.layoutType,
            value:
              layoutTypes.find(
                (type) => type.id === props.content.layout_type_id,
              )?.name ?? '',
          },
          {
            label: lang.screenConfiguration,
            value: getScreenConfigurationLabel(props.content.screens) ?? '',
          },
          {
            label: sectionTypes?.length
              ? lang.assemblyLayouts.advancedSettings.title
              : '',
            value: sectionTypes?.length
              ? advancedSettings(naturalSort(sectionTypes))
              : '',
          },
        ];

      default:
        return [];
    }
  })();

  const buttons = [];

  if (props.type === 'layout' && !isViewOnly) {
    buttons.push(
      <Button
        endIcon={<EditIcon />}
        component={Link}
        to={`edit/${props.content.id}`}
        disabled={
          props.hasStageAssembly ||
          ('status' in props.content && props.content.status === IStatus.READY)
        }>
        {lang.edit}
      </Button>,
    );
  } else if (props.type !== 'layout') {
    buttons.push(
      <Button
        variant={isViewOnly ? 'contained' : 'outlined'}
        component={Link}
        to={`details/${props.content.id}`}>
        {lang.viewDetails}
      </Button>,
    );
    if (!isViewOnly) {
      buttons.push(
        <Button
          endIcon={<EditIcon />}
          disabled={
            props.hasStageAssembly ||
            ('status' in props.content &&
              props.content.status === IStatus.READY)
          }
          component={Link}
          to={`edit/${props.content.id}`}
          {...('component_type_id' in props.content && {
            onClick: () => enterEditComponentFromInfoEvent(props.content.id),
          })}
          {...(props.type === 'assembly' && {
            onClick: () => assemblyEditFromInfoEvent(props.content.id),
          })}>
          {lang.edit}
        </Button>,
      );
    }
  }

  return (
    <Modal {...props} fullWidth maxWidth="xl">
      <IconButton
        aria-label="close"
        onClick={props.onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}>
        <CloseIcon />
      </IconButton>

      <CustomTooltip
        width={500}
        placement="bottom"
        title={props.content.name}
        overflowLineNumber={1}>
        <DialogTitle
          variant="h2"
          aria-label="preview-dialog-title"
          sx={{
            padding: '0',
            margin: '16px 16px 16px 30px',
          }}>
          {props.content.name}
        </DialogTitle>
      </CustomTooltip>

      <StyledDialogContent>
        <SubTitleContainer>
          {getSubTitles.map((subTitle) => (
            <PreviewModalSubTitle
              key={subTitle.label}
              label={subTitle.label}
              value={subTitle.value}
            />
          ))}
          {props.type === 'assembly' && (
            <DynamicTooltipChip
              chipName={
                props.content.variation_parent_id
                  ? `${lang.variationAssembly}`
                  : `${lang.tooltipMessages.originalAssembly}`
              }
              variant="filled"
              color={props.content.variation_parent_id ? 'warning' : 'default'}
              size="small"
            />
          )}
        </SubTitleContainer>

        <StyledGrid container>
          <Grid item xs={12} ref={containerRef}>
            {(() => {
              switch (props.type) {
                case 'component':
                  return (
                    <ScaleContainer
                      containerStyle={{
                        width: '100%',
                        height: '50vh',
                      }}>
                      <PreviewModalContent
                        type={props.type}
                        containerSize={containerSize}
                        content={{
                          ...props.content,
                          theme: themes.find(
                            (theme) => theme.id === props?.content?.theme_id,
                          ),
                        }}
                        productFilters={props.productFilters}
                        productVariationFilters={props.productVariationFilters}
                        skipProductsApiOnEmptyFilters={
                          props.skipProductsApiOnEmptyFilters
                        }
                      />
                    </ScaleContainer>
                  );

                case 'product':
                  return (
                    <PreviewModalContent
                      type={props.type}
                      content={props.content}
                    />
                  );
                case 'layout':
                  return (
                    <PreviewModalContent
                      type={props.type}
                      containerSize={containerSize}
                      content={props.content}
                    />
                  );
                default: {
                  const isLoadingModalPreview =
                    props?.isLoadingModalPreview || false;
                  const hasModalPreviewLoaded =
                    props?.hasModalPreviewLoaded || true;

                  if (isLoadingModalPreview) {
                    return <Progress />;
                  }

                  return (
                    hasModalPreviewLoaded && (
                      <ScaleContainer
                        containerStyle={{
                          width: '100%',
                          height: '50vh',
                        }}>
                        <PreviewModalContent
                          type={props.type}
                          containerSize={containerSize}
                          content={props.content}
                          assemblyComponents={props.assemblyComponents}
                          layout={props.layout}
                          productFilters={props.productFilters}
                          productVariationFilters={
                            props.productVariationFilters
                          }
                          skipProductsApiOnEmptyFilters={
                            props.skipProductsApiOnEmptyFilters
                          }
                        />
                      </ScaleContainer>
                    )
                  );
                }
              }
            })()}
          </Grid>
        </StyledGrid>
      </StyledDialogContent>

      {!props.hideButtons && (
        <DialogActions sx={{ paddingRight: '24px' }}>
          {...buttons}
        </DialogActions>
      )}
    </Modal>
  );
};
